import React, { useCallback, useContext, useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Copy, Trash2, History, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { GateContext } from "@/context/GateContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SCOPES = [
  { key: "package", title: "Professional Case Package", inc: "Timeline + evidence list + summary", exc: "Raw private notes" },
  { key: "timeline_reports", title: "Timeline & Reports Only", inc: "Court-admissible facts & chronology", exc: "Source files / downloads" },
  { key: "selected_evidence", title: "Selected Evidence Only", inc: "Only the exhibits you choose", exc: "Timeline & other files" },
  { key: "complete", title: "Complete Matter File", inc: "Timeline + all evidence + financials", exc: "Nothing — full read-only file" },
];
const WARNING = "\u26A0\uFE0F COURT FILING & COMMUNICATION WARNING: Professional Review Links are not a substitute for formal filing, service, or court-approved submission procedures. Do not send a review link directly to a judge, magistrate, or court unless the court has specifically authorized that method of submission.";

export default function ShareModal({ open, onOpenChange, matterId }) {
  const { gate } = useContext(GateContext);
  const [scope, setScope] = useState("package");
  const [expiration, setExpiration] = useState("14d");
  const [requireEmail, setRequireEmail] = useState(false);
  const [allowDownloads, setAllowDownloads] = useState(true);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState([]);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [selectedEv, setSelectedEv] = useState([]);

  const loadLinks = useCallback(async () => {
    if (!matterId) return;
    try { const { data } = await api.get("/shares", { params: { matter_id: matterId } }); setLinks(data); }
    catch { /* ignore */ }
  }, [matterId]);

  useEffect(() => { if (open) { loadLinks(); setAck(false); setHistory(null); setHistoryFor(null); } }, [open, loadLinks]);

  useEffect(() => {
    if (scope === "selected_evidence" && evidence.length === 0) {
      api.get("/evidence", { params: { matter_id: matterId } }).then(({ data }) => setEvidence(data)).catch((e) => console.error("load evidence failed", e));
    }
  }, [scope, matterId, evidence.length]);

  const create = async () => {
    if (!ack) { toast.message("Please acknowledge the court-filing warning"); return; }
    if (!gate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/shares", {
        matter_id: matterId, scope, expiration, require_email: requireEmail,
        allow_downloads: allowDownloads, court_ack: ack,
        evidence_ids: scope === "selected_evidence" ? selectedEv : [],
      });
      const full = `${window.location.origin}${data.path}`;
      try { await navigator.clipboard.writeText(full); } catch { /* ignore */ }
      toast.success("Review link created & copied", { description: full });
      setAck(false); setSelectedEv([]);
      loadLinks();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create link"); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await api.post(`/shares/${id}/revoke`); toast.success("Access revoked"); loadLinks(); }
    catch { toast.error("Could not revoke"); }
  };

  const viewHistory = async (id) => {
    setHistoryFor(id); setHistory(null);
    try { const { data } = await api.get(`/shares/${id}/history`); setHistory(data); }
    catch { toast.error("Could not load history"); }
  };

  const copyLink = async (token) => {
    const full = `${window.location.origin}/review/${token}`;
    try { await navigator.clipboard.writeText(full); toast.success("Link copied"); } catch { toast.message(full); }
  };

  const sc = SCOPES.find((s) => s.key === scope);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Secure Professional Review™
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Create an expiring, read-only link for an attorney or reviewer.</DialogDescription>
        </DialogHeader>

        {/* Court warning */}
        <div className="rounded-md border p-3 flex gap-3" style={{ background: "rgba(245,158,11,0.10)", borderColor: "rgba(245,158,11,0.4)" }} data-testid="court-warning-box">
          <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: "var(--ek-warning)" }} />
          <p className="text-xs leading-relaxed" style={{ color: "var(--ek-text-secondary)" }}>{WARNING}</p>
        </div>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>What to share</Label>
            <div className="grid grid-cols-1 gap-2">
              {SCOPES.map((s) => (
                <button key={s.key} type="button" onClick={() => setScope(s.key)} data-testid={`scope-${s.key}`}
                  className="text-left rounded-md border p-2.5 transition-colors"
                  style={{ background: scope === s.key ? "rgba(46,130,246,0.10)" : "transparent", borderColor: scope === s.key ? "var(--ek-primary)" : "var(--ek-border)" }}>
                  <div className="text-sm font-medium" style={{ color: "var(--ek-text)" }}>{s.title}</div>
                </button>
              ))}
            </div>
          </div>

          {scope === "selected_evidence" && (
            <div className="space-y-1 max-h-40 overflow-y-auto border rounded-md p-2" style={{ borderColor: "var(--ek-border)" }} data-testid="scope-evidence-picker">
              {evidence.length === 0 ? <p className="text-xs p-2" style={{ color: "var(--ek-text-muted)" }}>No evidence in this case.</p> :
                evidence.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 p-1.5 cursor-pointer">
                    <Checkbox checked={selectedEv.includes(e.id)} onCheckedChange={(v) => setSelectedEv((s) => v ? [...s, e.id] : s.filter((x) => x !== e.id))} data-testid={`pick-ev-${e.id}`} />
                    <span className="text-xs truncate" data-private>{e.filename}</span>
                  </label>
                ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Expiration</Label>
              <Select value={expiration} onValueChange={setExpiration}>
                <SelectTrigger data-testid="share-expiry-select" className="bg-transparent h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="48h" data-testid="expiry-48h">48 hours</SelectItem>
                  <SelectItem value="7d" data-testid="expiry-7d">7 days</SelectItem>
                  <SelectItem value="14d" data-testid="expiry-14d">14 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 sm:pt-6 cursor-pointer">
              <Checkbox checked={requireEmail} onCheckedChange={(v) => setRequireEmail(!!v)} data-testid="toggle-require-email" />
              <span className="text-xs" style={{ color: "var(--ek-text-secondary)" }}>Require email</span>
            </label>
            <label className="flex items-center gap-2 sm:pt-6 cursor-pointer">
              <Checkbox checked={allowDownloads} onCheckedChange={(v) => setAllowDownloads(!!v)} data-testid="toggle-allow-downloads" />
              <span className="text-xs" style={{ color: "var(--ek-text-secondary)" }}>Allow downloads</span>
            </label>
          </div>

          {/* Summary box */}
          <div className="rounded-md border p-3 text-xs space-y-1" style={{ background: "var(--ek-surface-hover)", borderColor: "var(--ek-border)" }} data-testid="share-summary-box">
            <div className="ek-mono-label">THIS LINK WILL INCLUDE</div>
            <div style={{ color: "var(--ek-secure)" }}>✓ {sc.inc}</div>
            <div style={{ color: "var(--ek-text-muted)" }}>✕ Excluded: {sc.exc}</div>
            <div style={{ color: "var(--ek-text-secondary)" }}>Expires in {expiration === "48h" ? "48 hours" : expiration === "7d" ? "7 days" : "14 days"} · Downloads {allowDownloads ? "on" : "off"} · Email {requireEmail ? "required" : "not required"}</div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer" data-testid="court-ack-label">
            <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="court-ack-checkbox" className="mt-0.5" />
            <span className="text-sm" style={{ color: "var(--ek-text)" }}>I understand and will follow my local court rules</span>
          </label>

          <Button onClick={create} disabled={busy || !ack || (scope === "selected_evidence" && selectedEv.length === 0)} data-testid="create-share-btn" className="rounded-md w-full" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {busy ? "CREATING…" : "Create secure review link"}
          </Button>
        </div>

        {links.length > 0 && (
          <div className="space-y-2 border-t pt-3" style={{ borderColor: "var(--ek-border)" }} data-testid="share-links-list">
            <div className="ek-mono-label">ACTIVE & PAST LINKS</div>
            {links.map((l) => (
              <div key={l.id} className="rounded-md border p-2.5" style={{ borderColor: "var(--ek-border)" }} data-testid={`share-link-${l.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{l.scope_label}</div>
                    <div className="ek-mono-label mt-0.5">{l.status.toUpperCase()} · {l.open_count} OPENS · {l.download_count} DOWNLOADS</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copyLink(l.token)} data-testid={`copy-link-${l.id}`} className="p-1.5" title="Copy link"><Copy className="h-4 w-4" style={{ color: "var(--ek-primary)" }} /></button>
                    <button onClick={() => viewHistory(l.id)} data-testid={`history-btn-${l.id}`} className="p-1.5" title="View history"><History className="h-4 w-4" style={{ color: "var(--ek-steel)" }} /></button>
                    {l.status === "active" && <button onClick={() => revoke(l.id)} data-testid={`revoke-btn-${l.id}`} className="p-1.5" title="Revoke"><Trash2 className="h-4 w-4" style={{ color: "var(--ek-error)" }} /></button>}
                  </div>
                </div>
                {historyFor === l.id && history && (
                  <div className="mt-2 pt-2 border-t text-xs space-y-1" style={{ borderColor: "var(--ek-border)" }} data-testid={`history-${l.id}`}>
                    {history.opens.length === 0 && history.downloads.length === 0 ? <span style={{ color: "var(--ek-text-muted)" }}>No activity yet.</span> : null}
                    {history.opens.map((o, i) => <div key={`o${i}`} style={{ color: "var(--ek-text-secondary)" }}>👁 Opened {(o.at || "").slice(0, 16).replace("T", " ")} {o.email ? `· ${o.email}` : ""}</div>)}
                    {history.downloads.map((d, i) => <div key={`d${i}`} style={{ color: "var(--ek-text-secondary)" }}>⬇ {d.filename} {(d.at || "").slice(0, 16).replace("T", " ")}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
